import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
    useInfiniteQuery,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import DatePicker from "react-datepicker";
import Drawer from "react-modern-drawer";
import { AsyncPaginate } from "react-select-async-paginate";

import {
    fetchAccountBillingRates,
    fetchAccounts,
    fetchAllBankAccounts,
    fetchBillRuns,
    fetchFinanceOpsJobs,
    fetchFundingRequests,
    fetchPaymentCycles,
    fetchPayeeBankAccountsFromDB,
    getBillingCycles,
    postFinanceOpsJob,
    postFinanceOpsReadiness,
    patchFinanceOpsJob,
    triggerBillRun,
} from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import Modal from "../modal/Modal";
import "./FinanceOpsDashboard.scss";

const JOB_TYPE_BILL_RUN = "bill-run";
const INFO_DRAWER_SIZE = 440;
const AUDIT_JOB_LIMIT = 50;
const ACCOUNT_PAGE_SIZE = 40;

const accountOptionFromParams = (accountKeyParam, accountHccIdParam, accountNameParam) => {
    if (!accountKeyParam) return null;
    const key = Number(accountKeyParam);
    if (!Number.isFinite(key)) return null;
    return {
        value: accountHccIdParam || accountKeyParam,
        label: accountNameParam
            ? `${accountNameParam}${accountHccIdParam ? ` (${accountHccIdParam})` : ""}`
            : accountHccIdParam || `Account ${key}`,
        additional: {
            accountKey: key,
            accountHccId: accountHccIdParam || undefined,
            accountName: accountNameParam || undefined,
        },
    };
};

const TABS = [
    { id: "accounts", label: "Accounts" },
    { id: "exceptions", label: "AR / exceptions" },
    { id: "audit", label: "Audit" },
];

const EXCEPTION_FILTERS = [
    { id: "all", label: "All" },
    { id: "needs-review", label: "Needs review" },
    { id: "open-bill", label: "Open bills" },
    { id: "open-fr", label: "Open funding" },
    { id: "failed-job", label: "Failed jobs" },
];

const JOB_TYPE_LABELS = {
    "bill-run": "Bill run",
    "payment-run": "Payment run",
    "funding-payment": "Funding payment",
    "premium-payment": "Premium payment",
    "funding-adjustment": "Funding adjustment",
    "billing-adjustment": "Billing adjustment",
    "invoice-pack": "Invoice pack",
    "registry-sync": "Registry sync",
};

const jobTypeLabel = (type) => JOB_TYPE_LABELS[type] || type || "—";

const money = (n) => {
    if (n == null || !Number.isFinite(Number(n))) return "—";
    return Number(n).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
    });
};

const ageFromDue = (dueDate) => {
    if (!dueDate) return { band: "—", days: null, sort: -1 };
    const due = new Date(`${String(dueDate).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(due.getTime())) return { band: "—", days: null, sort: -1 };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.floor((today.getTime() - due.getTime()) / 86_400_000);
    if (days <= 0) return { band: "Current", days, sort: 0 };
    if (days <= 30) return { band: "1–30", days, sort: 1 };
    if (days <= 60) return { band: "31–60", days, sort: 2 };
    return { band: "61+", days, sort: 3 };
};

const buildExceptionRows = ({ needsReview, bills, funding, jobs }) => {
    const rows = [];
    const needsReviewKeys = new Set();

    for (const b of needsReview || []) {
        const key = b.key ?? b.billFactKey;
        if (key != null) needsReviewKeys.add(String(key));
        const age = ageFromDue(b.dueDate);
        rows.push({
            id: `nr-${key}`,
            kind: "needs-review",
            kindLabel: "Needs review",
            accountName: b.accountName,
            accountHccId: b.accountHccId,
            accountKey: b.accountKey,
            recordLabel: `Bill #${key}`,
            dueDate: b.dueDate || null,
            amountDue: b.totalDue,
            statusLabel: b.statusLabel || b.paidStatus || "Needs Review",
            ageBand: age.band,
            ageSort: age.sort,
            href: `/finance/bills/${key}`,
        });
    }

    for (const b of bills || []) {
        const key = b.key ?? b.billFactKey;
        if (key != null && needsReviewKeys.has(String(key))) continue;
        const code = String(b.paidStatusCode || "").toLowerCase();
        if (code !== "unpaid" && code !== "partial") continue;
        const age = ageFromDue(b.dueDate);
        rows.push({
            id: `bill-${key}`,
            kind: "open-bill",
            kindLabel: "Open bill",
            accountName: b.accountName,
            accountHccId: b.accountHccId,
            accountKey: b.accountKey,
            recordLabel: `Bill #${key}`,
            dueDate: b.dueDate || null,
            amountDue: b.totalDue,
            statusLabel: b.paidStatus || b.statusLabel || code,
            ageBand: age.band,
            ageSort: age.sort,
            href: `/finance/bills/${key}`,
        });
    }

    for (const fr of funding || []) {
        const key = fr.key ?? fr.fundingRequestFactKey;
        const code = String(fr.paidStatusCode || "").toLowerCase();
        if (code !== "unpaid" && code !== "partial") continue;
        const due =
            Number(fr.totalDue) -
            (Number.isFinite(Number(fr.totalPayments)) ? Number(fr.totalPayments) : 0);
        const amountDue = Number.isFinite(due) ? Math.max(0, due) : fr.totalDue;
        const age = ageFromDue(fr.dueDate);
        rows.push({
            id: `fr-${key}`,
            kind: "open-fr",
            kindLabel: "Open funding",
            accountName: fr.accountName,
            accountHccId: fr.accountHccId,
            accountKey: fr.accountKey,
            recordLabel: `FR #${key}`,
            dueDate: fr.dueDate || null,
            amountDue,
            statusLabel: fr.paidStatus || fr.statusLabel || code,
            ageBand: age.band,
            ageSort: age.sort,
            href: `/finance/funding-requests/${key}`,
        });
    }

    for (const job of jobs || []) {
        const status = String(job.status || "").toLowerCase();
        if (status !== "failed" && status !== "blocked") continue;
        rows.push({
            id: `job-${job.id}`,
            kind: "failed-job",
            kindLabel: status === "blocked" ? "Blocked job" : "Failed job",
            accountName: job.accountName,
            accountHccId: job.accountHccId,
            accountKey: job.accountKey,
            recordLabel: jobTypeLabel(job.type),
            dueDate: null,
            amountDue: null,
            statusLabel: job.status,
            ageBand: "—",
            ageSort: 4,
            href: `/finance/ops?tab=audit`,
            error: job.error || null,
        });
    }

    rows.sort((a, b) => {
        if (b.ageSort !== a.ageSort) return b.ageSort - a.ageSort;
        const ad = a.dueDate || "";
        const bd = b.dueDate || "";
        if (ad !== bd) return ad.localeCompare(bd);
        return String(a.recordLabel).localeCompare(String(b.recordLabel));
    });
    return rows;
};

const FinanceOpsInfo = () => (
    <div className="finance-ops__panel-guide">
        <p className="finance-ops__guide-intro">
            Account readiness, AR / exceptions queue, durable job audit, and bill-run triggers. HRP
            remains system of record — this board orchestrates and watches.
        </p>
        <section>
            <h3>Phase 1 slice</h3>
            <ol>
                <li>
                    <strong>Accounts</strong> — browse / multi-select accounts, queue bill runs,
                    and open a row for readiness checks and jump links.
                </li>
                <li>
                    <strong>AR / exceptions</strong> — Needs Review bills, unpaid / partial AR,
                    open funding requests, and failed finance jobs.
                </li>
                <li>
                    <strong>Audit</strong> — durable log of who ran bill runs, payments, and
                    adjustments (with HRP correlation ids).
                </li>
            </ol>
        </section>
        <p className="finance-ops__guide-note">
            Queue actions from <strong>Accounts</strong>. Work the{" "}
            <strong>AR / exceptions</strong> queue, then confirm outcomes under{" "}
            <strong>Audit</strong>.
        </p>
    </div>
);

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const statusPill = (status) => {
    const s = String(status || "ok").toLowerCase();
    return `finance-ops__pill finance-ops__pill--${s}`;
};

const accountLinks = (pilot) => {
    const params = new URLSearchParams();
    if (pilot.accountKey) params.set("accountKey", String(pilot.accountKey));
    if (pilot.accountHccId) params.set("accountHccId", pilot.accountHccId);
    if (pilot.accountName) params.set("accountName", pilot.accountName);
    const qs = params.toString();
    return {
        bills: `/finance/bills${qs ? `?${qs}` : ""}`,
        funding: `/finance/funding-requests${qs ? `?${qs}` : ""}`,
        cycles: `/finance/payment-cycles${qs ? `?${qs}` : ""}`,
        rates: `/finance/billing-rates${qs ? `?${qs}` : ""}`,
    };
};

const loadReadinessForPilot = async (pilot, asOf) => {
    const accountKey = pilot.accountKey;
    const [billingRates, paymentCycles, bankAccounts, payeeBankAccounts, openBills, openFundingRequests] =
        await Promise.all([
            fetchAccountBillingRates({ accountKey, asOfDate: asOf }).catch(() => ({ items: [] })),
            fetchPaymentCycles({ accountId: accountKey }).catch(() => ({ items: [] })),
            fetchAllBankAccounts({ limit: 25 }).catch(() => ({ items: [] })),
            fetchPayeeBankAccountsFromDB({ limit: 25 }).catch(() => ({ Items: [] })),
            fetchBillRuns({ accountKey, asOfDate: asOf, limit: 10, offset: 0 }).catch(() => ({ items: [] })),
            fetchFundingRequests({ accountKey, asOfDate: asOf, limit: 10, offset: 0 }).catch(() => ({
                items: [],
            })),
        ]);

    return postFinanceOpsReadiness({
        pilot,
        accountKey: pilot.accountKey,
        accountHccId: pilot.accountHccId,
        accountName: pilot.accountName,
        billingRates,
        paymentCycles,
        bankAccounts: bankAccounts?.items || bankAccounts?.rows || bankAccounts,
        payeeBankAccounts: payeeBankAccounts?.Items || payeeBankAccounts?.items || payeeBankAccounts,
        openBills: openBills?.items || openBills,
        openFundingRequests: openFundingRequests?.items || openFundingRequests,
    });
};

const FinanceOpsDashboard = () => {
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [infoOpen, setInfoOpen] = useState(false);
    const [auditTypeFilter, setAuditTypeFilter] = useState("all");
    const [exceptionFilter, setExceptionFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    const [detailAccount, setDetailAccount] = useState(null);
    const { ref: loadMoreRef, inView } = useInView();
    const asOf = toIsoDate(asOfDate);
    const tabParam = searchParams.get("tab");
    const accountKeyParam = searchParams.get("accountKey");
    const accountHccIdParam = searchParams.get("accountHccId");
    const accountNameParam = searchParams.get("accountName");
    const normalizedTab =
        tabParam === "pilots" ? "accounts" : tabParam;
    const tab = TABS.some((t) => t.id === normalizedTab) ? normalizedTab : "accounts";

    const [account, setAccount] = useState(() =>
        accountOptionFromParams(accountKeyParam, accountHccIdParam, accountNameParam),
    );

    useEffect(() => {
        setAccount(
            accountOptionFromParams(accountKeyParam, accountHccIdParam, accountNameParam),
        );
    }, [accountKeyParam, accountHccIdParam, accountNameParam]);

    const filterAccountKey = account?.additional?.accountKey
        ? Number(account.additional.accountKey)
        : null;

    const setTab = (next) => {
        const params = new URLSearchParams(searchParams);
        if (next === "accounts") params.delete("tab");
        else params.set("tab", next);
        setSearchParams(params, { replace: true });
    };

    const onAccountChange = (opt) => {
        setAccount(opt);
        setSelectedKeys(new Set());
        const next = new URLSearchParams(searchParams);
        if (!opt?.additional?.accountKey) {
            next.delete("accountKey");
            next.delete("accountHccId");
            next.delete("accountName");
        } else {
            next.set("accountKey", String(opt.additional.accountKey));
            if (opt.additional.accountHccId || opt.value) {
                next.set("accountHccId", String(opt.additional.accountHccId || opt.value));
            }
            if (opt.additional.accountName) {
                next.set("accountName", opt.additional.accountName);
            } else if (opt.label) {
                next.set("accountName", String(opt.label).replace(/\s*\([^)]*\)\s*$/, "").trim());
            }
        }
        setSearchParams(next, { replace: true });
    };

    const clearAccountFilters = () => {
        setAccount(null);
        setAsOfDate(new Date());
        setSearch("");
        setSearchApplied("");
        setSelectedKeys(new Set());
        const next = new URLSearchParams(searchParams);
        next.delete("accountKey");
        next.delete("accountHccId");
        next.delete("accountName");
        setSearchParams(next, { replace: true });
    };

    const {
        data: accountsPages,
        isLoading: accountsLoading,
        isFetching: accountsFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error: accountsError,
        status: accountsStatus,
    } = useInfiniteQuery({
        queryKey: [
            "finance-ops-accounts",
            filterAccountKey || null,
            searchApplied || "",
        ],
        queryFn: async ({ pageParam = 0 }) => {
            const data = await fetchAccounts({
                limit: ACCOUNT_PAGE_SIZE,
                offset: pageParam,
                accountLevel: 0,
                ...(filterAccountKey
                    ? { accountKey: filterAccountKey }
                    : searchApplied
                      ? { accountName: searchApplied }
                      : {}),
            });
            const rows = Array.isArray(data?.rows) ? data.rows : [];
            return {
                total: Number(data?.count) || rows.length,
                items: rows
                    .map((r) => ({
                        accountKey: r.accountKey != null ? Number(r.accountKey) : null,
                        accountHccId: r.accountHccId || null,
                        accountName: r.accountName || null,
                    }))
                    .filter((a) => a.accountKey != null),
            };
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((sum, p) => sum + (p?.items?.length || 0), 0);
            const total = lastPage?.total ?? 0;
            if (loaded >= total) return undefined;
            if ((lastPage?.items?.length || 0) < ACCOUNT_PAGE_SIZE) return undefined;
            return loaded;
        },
        enabled: tab === "accounts",
        staleTime: 60_000,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const accountRows = useMemo(
        () => (accountsPages?.pages || []).flatMap((p) => p?.items || []),
        [accountsPages],
    );

    const readinessQuery = useQuery({
        queryKey: [
            "finance-ops-readiness",
            asOf,
            detailAccount?.accountKey ?? null,
        ],
        enabled: !!detailAccount?.accountKey,
        queryFn: async () => {
            try {
                return await loadReadinessForPilot(detailAccount, asOf);
            } catch (err) {
                return {
                    accountKey: detailAccount.accountKey,
                    accountHccId: detailAccount.accountHccId,
                    accountName: detailAccount.accountName,
                    overall: "block",
                    canTriggerBillRun: false,
                    canTriggerPaymentRun: false,
                    checks: [
                        {
                            id: "load",
                            status: "block",
                            label: "Load",
                            detail: err.message || "Failed to load HRP snapshots",
                        },
                    ],
                    summary: {},
                    evaluatedAt: new Date().toISOString(),
                };
            }
        },
    });

    const jobsQuery = useQuery({
        queryKey: ["finance-ops-jobs"],
        queryFn: () => fetchFinanceOpsJobs({ limit: AUDIT_JOB_LIMIT }),
        refetchInterval: tab === "audit" || tab === "exceptions" ? 10_000 : 30_000,
    });

    const exceptionsQuery = useQuery({
        queryKey: ["finance-ops-exceptions"],
        queryFn: async () => {
            // Aging is vs today; DW as-of defaults to current date in the connector.
            const [needsReviewRes, billsRes, fundingRes, jobsRes] = await Promise.all([
                fetchBillRuns({
                    status: "rv",
                    sortBy: "dueDate",
                    sortDir: "asc",
                    limit: 40,
                    offset: 0,
                }).catch(() => ({ items: [] })),
                fetchBillRuns({
                    sortBy: "dueDate",
                    sortDir: "asc",
                    limit: 80,
                    offset: 0,
                }).catch(() => ({ items: [] })),
                fetchFundingRequests({
                    limit: 80,
                    offset: 0,
                }).catch(() => ({ items: [] })),
                fetchFinanceOpsJobs({ limit: AUDIT_JOB_LIMIT }).catch(() => ({ items: [] })),
            ]);
            return buildExceptionRows({
                needsReview: needsReviewRes?.items || [],
                bills: billsRes?.items || [],
                funding: fundingRes?.items || [],
                jobs: jobsRes?.items || [],
            });
        },
        staleTime: 30_000,
    });

    const queueBillRunForPilot = async (pilot, readinessIn) => {
        let readiness = readinessIn;
        if (!readiness) {
            try {
                readiness = await loadReadinessForPilot(pilot, asOf);
            } catch (err) {
                readiness = {
                    overall: "block",
                    canTriggerBillRun: false,
                    error: err.message || "Failed to load readiness",
                };
            }
        }

        const [billSample, billingCycles] = await Promise.all([
            fetchBillRuns({
                accountKey: pilot.accountKey,
                asOfDate: asOf,
                limit: 5,
                offset: 0,
            }).catch(() => ({ items: [] })),
            getBillingCycles({ limit: 50, offset: 0 }).catch(() => ({ rows: [] })),
        ]);
        const billRows = billSample?.items || billSample?.rows || [];
        const cycleRows = Array.isArray(billingCycles?.rows)
            ? billingCycles.rows
            : Array.isArray(billingCycles?.items)
              ? billingCycles.items
              : [];
        const cycleId =
            pilot.billingCycleName ||
            billRows.find((b) => b.billingCycleName)?.billingCycleName ||
            cycleRows.find((c) => String(c.manualRun || "").toUpperCase() === "Y")
                ?.billingCycleName ||
            cycleRows[0]?.billingCycleName ||
            null;

        const job = await postFinanceOpsJob({
            type: JOB_TYPE_BILL_RUN,
            accountKey: pilot.accountKey,
            accountHccId: pilot.accountHccId,
            accountName: pilot.accountName,
            cycleId: cycleId || undefined,
            period: asOf,
            meta: { overall: readiness?.overall, asOf },
        });

        if (!readiness?.canTriggerBillRun) {
            return patchFinanceOpsJob(job.id, {
                status: "blocked",
                error:
                    readiness?.error ||
                    "Readiness blocked bill run (missing rates or load failure).",
            });
        }

        if (!cycleId) {
            return patchFinanceOpsJob(job.id, {
                status: "blocked",
                error: "No billing cycle name found for triggerBillRun.",
            });
        }

        await patchFinanceOpsJob(job.id, { status: "running", cycleId });
        try {
            const hrpResult = await triggerBillRun({ cycleId });
            const cvc = hrpResult?.cvcId ?? hrpResult?.result?.cvcId ?? null;
            const cvcId = Array.isArray(cvc) ? cvc[0] : cvc;
            return patchFinanceOpsJob(job.id, {
                status: "succeeded",
                hrpCorrelationId: cvcId != null ? String(cvcId) : null,
                meta: { ...(job.meta || {}), hrpResult },
            });
        } catch (err) {
            return patchFinanceOpsJob(job.id, {
                status: "failed",
                error: err.message || "triggerBillRun failed",
            });
        }
    };

    const billRunMutation = useMutation({
        mutationFn: async ({ pilot, readiness }) =>
            queueBillRunForPilot(pilot, readiness),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
        },
    });

    const batchBillRunMutation = useMutation({
        mutationFn: async (pilots) => {
            const results = [];
            for (const pilot of pilots) {
                results.push(await queueBillRunForPilot(pilot));
            }
            return results;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] });
            setSelectedKeys(new Set());
        },
    });

    const readiness = readinessQuery.data || null;
    const jobs = jobsQuery.data?.items || [];
    const exceptionRows = exceptionsQuery.data || [];

    const readinessLinks = useMemo(() => {
        if (!detailAccount) return null;
        return accountLinks({
            ...detailAccount,
            accountName: detailAccount.accountName || readiness?.accountName || null,
        });
    }, [detailAccount, readiness?.accountName]);

    const selectedPilots = useMemo(
        () => accountRows.filter((r) => selectedKeys.has(r.accountKey)),
        [accountRows, selectedKeys],
    );

    const allVisibleSelected =
        accountRows.length > 0 &&
        accountRows.every((r) => selectedKeys.has(r.accountKey));

    const toggleSelectAllVisible = () => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (allVisibleSelected) {
                accountRows.forEach((r) => next.delete(r.accountKey));
            } else {
                accountRows.forEach((r) => next.add(r.accountKey));
            }
            return next;
        });
    };

    const toggleSelectRow = (accountKey) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(accountKey)) next.delete(accountKey);
            else next.add(accountKey);
            return next;
        });
    };

    const openAccountDetail = (row) => {
        setDetailAccount(row);
    };

    const closeAccountDetail = () => {
        setDetailAccount(null);
    };

    const auditTypeOptions = useMemo(() => {
        const types = [...new Set(jobs.map((j) => j.type).filter(Boolean))];
        return types.sort();
    }, [jobs]);

    const auditJobs = useMemo(() => {
        if (auditTypeFilter === "all") return jobs;
        return jobs.filter((j) => j.type === auditTypeFilter);
    }, [jobs, auditTypeFilter]);

    const filteredExceptions = useMemo(() => {
        if (exceptionFilter === "all") return exceptionRows;
        return exceptionRows.filter((r) => r.kind === exceptionFilter);
    }, [exceptionRows, exceptionFilter]);

    const exceptionCounts = useMemo(() => {
        const counts = { all: exceptionRows.length };
        for (const r of exceptionRows) {
            counts[r.kind] = (counts[r.kind] || 0) + 1;
        }
        return counts;
    }, [exceptionRows]);

    return (
        <div className="container finance-ops scrollable-form">
            <div className="finance-ops__page-head">
                <div>
                    <p className="title">Finance ops</p>
                    <p className="sub-title">
                        Multi-select accounts to queue bill runs; open a row for readiness checks.
                    </p>
                </div>
                <button
                    type="button"
                    className="finance-ops__guide-toggle"
                    onClick={() => setInfoOpen((open) => !open)}
                    aria-pressed={infoOpen}
                    aria-expanded={infoOpen}
                >
                    {infoOpen ? "Hide info" : "About finance ops"}
                </button>
            </div>

            <div className="finance-ops__tabs" role="tablist" aria-label="Finance ops sections">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        id={`finance-ops-tab-${t.id}`}
                        aria-selected={tab === t.id}
                        aria-controls={`finance-ops-panel-${t.id}`}
                        className={`finance-ops__tab${tab === t.id ? " finance-ops__tab--active" : ""}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                        {t.id === "exceptions" && exceptionCounts.all > 0 ? (
                            <span className="finance-ops__tab-count">{exceptionCounts.all}</span>
                        ) : null}
                        {t.id === "audit" && jobs.length > 0 ? (
                            <span className="finance-ops__tab-count">{jobs.length}</span>
                        ) : null}
                    </button>
                ))}
            </div>

            {tab === "accounts" && (
                <div
                    id="finance-ops-panel-accounts"
                    role="tabpanel"
                    aria-labelledby="finance-ops-tab-accounts"
                    className="finance-ops__tab-panel finance-ops__tab-panel--fill"
                >
                    <div className="finance-ops__controls">
                        <div className="finance-ops__filters">
                            <div className="finance-ops__field finance-ops__field--account">
                                <label>Account</label>
                                <AsyncPaginate
                                    placeholder="All accounts"
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    debounceTimeout={300}
                                    value={account}
                                    loadOptions={loadAccountHccIdentificationNumber}
                                    onChange={onAccountChange}
                                    menuPortalTarget={
                                        typeof document !== "undefined" ? document.body : null
                                    }
                                    styles={{
                                        ...customStyles,
                                        menuPortal: (base) => ({ ...base, zIndex: 200 }),
                                    }}
                                    isClearable
                                />
                            </div>
                            <div className="finance-ops__field finance-ops__field--date">
                                <label>As of</label>
                                <DatePicker
                                    selected={asOfDate}
                                    onChange={(d) => setAsOfDate(d || new Date())}
                                    dateFormat="yyyy-MM-dd"
                                    placeholderText="As of date"
                                    todayButton="Today"
                                    className="date-picker"
                                    wrapperClassName="date-picker-wrapper"
                                    portalId="root"
                                    popperPlacement="bottom-start"
                                    popperClassName="date-picker-popper"
                                    popperProps={{ strategy: "fixed" }}
                                />
                            </div>
                            <div className="finance-ops__field finance-ops__field--search">
                                <label>Search</label>
                                <div className="finance-ops__search-combo">
                                    <input
                                        type="text"
                                        className="finance-ops__search"
                                        placeholder="Account name"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                setSearchApplied(search.trim());
                                                setSelectedKeys(new Set());
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="finance-ops__search-btn"
                                        onClick={() => {
                                            setSearchApplied(search.trim());
                                            setSelectedKeys(new Set());
                                        }}
                                    >
                                        Search
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="finance-ops__toolbar">
                            <div className="finance-ops__toolbar-left">
                                <button
                                    type="button"
                                    className="finance-ops__link-btn finance-ops__link-btn--danger"
                                    onClick={clearAccountFilters}
                                >
                                    Clear filters
                                </button>
                                <p className="finance-ops__count">
                                    {selectedKeys.size > 0
                                        ? `${selectedKeys.size} selected`
                                        : `${accountRows.length} account${
                                              accountRows.length === 1 ? "" : "s"
                                          }`}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="finance-ops__primary-btn"
                                disabled={
                                    selectedPilots.length === 0 ||
                                    batchBillRunMutation.isPending
                                }
                                onClick={() =>
                                    batchBillRunMutation.mutate(selectedPilots)
                                }
                            >
                                {batchBillRunMutation.isPending
                                    ? "Queuing…"
                                    : selectedPilots.length > 1
                                      ? `Queue bill run (${selectedPilots.length})`
                                      : "Queue bill run"}
                            </button>
                        </div>
                    </div>

                    {accountsStatus === "error" ? (
                        <span className="danger">
                            Error:{" "}
                            {accountsError?.message || "Failed to load accounts"}
                        </span>
                    ) : null}

                    {batchBillRunMutation.isError ? (
                        <span className="danger">
                            Error:{" "}
                            {batchBillRunMutation.error?.message ||
                                "Failed to queue bill run(s)"}
                        </span>
                    ) : null}

                    {accountsLoading ? (
                        <p className="finance-ops__muted">Loading accounts…</p>
                    ) : accountRows.length === 0 ? (
                        <p className="finance-ops__muted">
                            No accounts match the current filters.
                        </p>
                    ) : (
                        <div className="table-wrapper finance-ops__accounts-table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: "40px", textAlign: "center" }}>
                                            <input
                                                type="checkbox"
                                                aria-label="Select all visible accounts"
                                                checked={allVisibleSelected}
                                                onChange={toggleSelectAllVisible}
                                            />
                                        </th>
                                        <th />
                                        <th>ACCOUNT_NAME</th>
                                        <th>ACCOUNT_HCC_ID</th>
                                        <th>ACCOUNT_KEY</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {accountRows.map((row, index) => {
                                        const checked = selectedKeys.has(row.accountKey);
                                        return (
                                            <tr
                                                className={`tr${checked ? " finance-ops__row--selected" : ""}`}
                                                key={row.accountKey}
                                            >
                                                <td
                                                    style={{
                                                        minWidth: "40px",
                                                        maxWidth: "60px",
                                                        textAlign: "center",
                                                        overflow: "visible",
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Select ${
                                                            row.accountName ||
                                                            row.accountHccId ||
                                                            row.accountKey
                                                        }`}
                                                        checked={checked}
                                                        onChange={() =>
                                                            toggleSelectRow(row.accountKey)
                                                        }
                                                    />
                                                </td>
                                                <td style={{ minWidth: "40px", maxWidth: "60px" }}>
                                                    {index + 1}
                                                </td>
                                                <td>{row.accountName || "-"}</td>
                                                <td>{row.accountHccId || "-"}</td>
                                                <td>{row.accountKey}</td>
                                                <td
                                                    className="clickable-link more"
                                                    style={{
                                                        width: "80px",
                                                        textAlign: "center",
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={() => openAccountDetail(row)}
                                                >
                                                    Detail
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div
                                style={{
                                    width: "100%",
                                    padding: "1rem 0",
                                    display:
                                        isFetchingNextPage || hasNextPage
                                            ? "block"
                                            : "none",
                                }}
                                ref={loadMoreRef}
                            >
                                <button
                                    type="button"
                                    onClick={() => fetchNextPage()}
                                    disabled={!hasNextPage || isFetchingNextPage}
                                    className="clickable-link"
                                    style={{
                                        padding: ".4rem 1rem",
                                        backgroundColor: "var(--white)",
                                        border: "none",
                                    }}
                                >
                                    {isFetchingNextPage
                                        ? "Loading more..."
                                        : hasNextPage
                                          ? "Load more"
                                          : "Done"}
                                </button>
                            </div>
                            {accountsFetching && !isFetchingNextPage ? (
                                <div style={{ padding: ".4rem" }}>IsFetching...</div>
                            ) : null}
                        </div>
                    )}

                    <Modal
                        isOpen={!!detailAccount}
                        onClose={closeAccountDetail}
                        className="finance-ops__detail-modal"
                    >
                        {detailAccount ? (
                            <div className="finance-ops__detail">
                                <header className="finance-ops__detail-head">
                                    <p className="finance-ops__detail-eyebrow">
                                        Account readiness
                                    </p>
                                    <h2>
                                        {detailAccount.accountName ||
                                            readiness?.accountName ||
                                            "Account"}
                                    </h2>
                                    <p className="finance-ops__sub">
                                        {[
                                            detailAccount.accountHccId,
                                            `key ${detailAccount.accountKey}`,
                                            asOf ? `as of ${asOf}` : null,
                                        ]
                                            .filter(Boolean)
                                            .join(" · ")}
                                    </p>
                                </header>

                                <dl className="finance-ops__detail-meta">
                                    <div>
                                        <dt>Overall</dt>
                                        <dd>
                                            {readinessQuery.isLoading ? (
                                                <span className="finance-ops__pill">
                                                    Loading
                                                </span>
                                            ) : (
                                                <span
                                                    className={statusPill(
                                                        readiness?.overall,
                                                    )}
                                                >
                                                    {readiness?.overall || "—"}
                                                </span>
                                            )}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Bill run</dt>
                                        <dd>
                                            {readinessQuery.isLoading
                                                ? "…"
                                                : readiness?.canTriggerBillRun
                                                  ? "Ready"
                                                  : "Blocked"}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt>Payment run</dt>
                                        <dd>
                                            {readinessQuery.isLoading
                                                ? "…"
                                                : readiness?.canTriggerPaymentRun
                                                  ? "Ready"
                                                  : "Blocked"}
                                        </dd>
                                    </div>
                                </dl>

                                {billRunMutation.isError ? (
                                    <p className="danger">
                                        {billRunMutation.error.message}
                                    </p>
                                ) : null}

                                <section className="finance-ops__detail-section finance-ops__detail-section--checks">
                                    <h3>Checks</h3>
                                    {readinessQuery.isLoading ? (
                                        <p className="finance-ops__muted">
                                            Running readiness checks…
                                        </p>
                                    ) : (
                                        <div className="table-wrapper finance-ops__detail-table-wrap">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>CHECK</th>
                                                        <th>DETAIL</th>
                                                        <th>STATUS</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(readiness?.checks || []).map(
                                                        (c) => (
                                                            <tr
                                                                className="tr"
                                                                key={c.id}
                                                            >
                                                                <td>{c.label}</td>
                                                                <td>{c.detail || "—"}</td>
                                                                <td>
                                                                    <span
                                                                        className={statusPill(
                                                                            c.status,
                                                                        )}
                                                                    >
                                                                        {c.status}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        ),
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>

                                <section className="finance-ops__detail-section finance-ops__detail-section--jump">
                                    <h3>Jump to</h3>
                                    <nav
                                        className="finance-ops__jump-links"
                                        aria-label="Related finance pages"
                                    >
                                        <Link
                                            to={
                                                readinessLinks?.bills ||
                                                "/finance/bills"
                                            }
                                            onClick={closeAccountDetail}
                                        >
                                            Bills
                                        </Link>
                                        <Link
                                            to={
                                                readinessLinks?.funding ||
                                                "/finance/funding-requests"
                                            }
                                            onClick={closeAccountDetail}
                                        >
                                            Funding requests
                                        </Link>
                                        <Link
                                            to={
                                                readinessLinks?.cycles ||
                                                "/finance/payment-cycles"
                                            }
                                            onClick={closeAccountDetail}
                                        >
                                            Payment cycles
                                        </Link>
                                        <Link
                                            to={
                                                readinessLinks?.rates ||
                                                "/finance/billing-rates"
                                            }
                                            onClick={closeAccountDetail}
                                        >
                                            Billing rates
                                        </Link>
                                    </nav>
                                </section>

                                <footer className="finance-ops__detail-footer">
                                    <button
                                        type="button"
                                        className="finance-ops__link-btn"
                                        onClick={() => {
                                            queryClient.invalidateQueries({
                                                queryKey: [
                                                    "finance-ops-readiness",
                                                ],
                                            });
                                        }}
                                    >
                                        Refresh checks
                                    </button>
                                    <button
                                        type="button"
                                        className="finance-ops__primary-btn"
                                        disabled={
                                            billRunMutation.isPending ||
                                            readinessQuery.isLoading ||
                                            !readiness
                                        }
                                        onClick={() =>
                                            billRunMutation.mutate({
                                                pilot: detailAccount,
                                                readiness,
                                            })
                                        }
                                    >
                                        {billRunMutation.isPending
                                            ? "Queuing…"
                                            : "Queue bill run"}
                                    </button>
                                </footer>
                            </div>
                        ) : null}
                    </Modal>
                </div>
            )}

            {tab === "exceptions" && (
                <div
                    id="finance-ops-panel-exceptions"
                    role="tabpanel"
                    aria-labelledby="finance-ops-tab-exceptions"
                    className="finance-ops__tab-panel finance-ops__tab-panel--fill"
                >
                    <p className="finance-ops__lead">
                        Cross-account queue of Needs Review bills, unpaid / partial AR, open
                        funding requests, and failed finance jobs. Aging uses due date vs today
                        (sample of recent DW rows — not a full ledger scan).
                    </p>

                    <div className="finance-ops__toolbar">
                        <p className="finance-ops__count">
                            {filteredExceptions.length} exception
                            {filteredExceptions.length === 1 ? "" : "s"}
                            {exceptionFilter !== "all"
                                ? ` · ${
                                      EXCEPTION_FILTERS.find((f) => f.id === exceptionFilter)
                                          ?.label || exceptionFilter
                                  }`
                                : ""}
                        </p>
                        <button
                            type="button"
                            className="finance-ops__link-btn"
                            onClick={() =>
                                queryClient.invalidateQueries({
                                    queryKey: ["finance-ops-exceptions"],
                                })
                            }
                        >
                            Refresh
                        </button>
                    </div>

                    <div
                        className="finance-ops__type-filters"
                        role="group"
                        aria-label="Exception type"
                    >
                        {EXCEPTION_FILTERS.map((f) => (
                            <button
                                key={f.id}
                                type="button"
                                className={`finance-ops__type-chip${
                                    exceptionFilter === f.id
                                        ? " finance-ops__type-chip--active"
                                        : ""
                                }`}
                                onClick={() => setExceptionFilter(f.id)}
                            >
                                {f.label}
                                {exceptionCounts[f.id] ? (
                                    <span className="finance-ops__chip-count">
                                        {exceptionCounts[f.id]}
                                    </span>
                                ) : null}
                            </button>
                        ))}
                    </div>

                    {exceptionsQuery.isError ? (
                        <span className="danger">
                            Error:{" "}
                            {exceptionsQuery.error?.message || "Failed to load exceptions"}
                        </span>
                    ) : null}

                    {exceptionsQuery.isLoading ? (
                        <p className="finance-ops__muted">Loading AR / exceptions…</p>
                    ) : filteredExceptions.length === 0 ? (
                        <p className="finance-ops__muted">
                            No exceptions in the current sample. Open Bills / Funding Requests for
                            full lists.
                        </p>
                    ) : (
                        <div className="finance-ops__queue" role="region" aria-label="AR exceptions">
                            <table className="finance-ops__queue-table finance-ops__queue-table--exceptions">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Account</th>
                                        <th>Record</th>
                                        <th>Due</th>
                                        <th>Age</th>
                                        <th className="num">Amount due</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredExceptions.map((row) => (
                                        <tr key={row.id}>
                                            <td>
                                                <span
                                                    className={`finance-ops__kind finance-ops__kind--${row.kind}`}
                                                >
                                                    {row.kindLabel}
                                                </span>
                                            </td>
                                            <td>
                                                <div>
                                                    {row.accountName ||
                                                        row.accountHccId ||
                                                        (row.accountKey != null
                                                            ? `Account ${row.accountKey}`
                                                            : "—")}
                                                </div>
                                                {row.accountName &&
                                                (row.accountHccId || row.accountKey != null) ? (
                                                    <div className="finance-ops__sub">
                                                        {row.accountHccId || row.accountKey}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td>
                                                {row.href ? (
                                                    <Link
                                                        className="finance-ops__record-link"
                                                        to={row.href}
                                                    >
                                                        {row.recordLabel}
                                                    </Link>
                                                ) : (
                                                    row.recordLabel
                                                )}
                                                {row.error ? (
                                                    <div className="finance-ops__sub finance-ops__error-cell">
                                                        {row.error}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="finance-ops__nowrap">
                                                {row.dueDate || "—"}
                                            </td>
                                            <td>
                                                <span
                                                    className={`finance-ops__age finance-ops__age--${String(
                                                        row.ageBand,
                                                    )
                                                        .toLowerCase()
                                                        .replace(/[^a-z0-9]+/g, "")}`}
                                                >
                                                    {row.ageBand}
                                                </span>
                                            </td>
                                            <td className="num finance-ops__nowrap">
                                                {money(row.amountDue)}
                                            </td>
                                            <td>{row.statusLabel || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {tab === "audit" && (
                <div
                    id="finance-ops-panel-audit"
                    role="tabpanel"
                    aria-labelledby="finance-ops-tab-audit"
                    className="finance-ops__tab-panel"
                >
                    <p className="finance-ops__lead">
                        Durable activity log — bill runs, payment runs, employer payments, and
                        adjustments. Persists across restarts; includes who submitted and HRP
                        correlation ids.
                    </p>

                    <div className="finance-ops__toolbar">
                        <p className="finance-ops__count">
                            {auditJobs.length} activit
                            {auditJobs.length === 1 ? "y" : "ies"}
                            {auditTypeFilter !== "all" ? ` · ${jobTypeLabel(auditTypeFilter)}` : ""}
                        </p>
                        <button
                            type="button"
                            className="finance-ops__link-btn"
                            onClick={() =>
                                queryClient.invalidateQueries({ queryKey: ["finance-ops-jobs"] })
                            }
                        >
                            Refresh
                        </button>
                    </div>

                    {auditTypeOptions.length > 0 && (
                        <div className="finance-ops__type-filters" role="group" aria-label="Activity type">
                            <button
                                type="button"
                                className={`finance-ops__type-chip${
                                    auditTypeFilter === "all" ? " finance-ops__type-chip--active" : ""
                                }`}
                                onClick={() => setAuditTypeFilter("all")}
                            >
                                All
                            </button>
                            {auditTypeOptions.map((type) => (
                                <button
                                    key={type}
                                    type="button"
                                    className={`finance-ops__type-chip${
                                        auditTypeFilter === type
                                            ? " finance-ops__type-chip--active"
                                            : ""
                                    }`}
                                    onClick={() => setAuditTypeFilter(type)}
                                >
                                    {jobTypeLabel(type)}
                                </button>
                            ))}
                        </div>
                    )}

                    {jobsQuery.isError ? (
                        <span className="danger">
                            Error: {jobsQuery.error?.message || "Failed to load audit log"}
                        </span>
                    ) : null}

                    {jobsQuery.isLoading ? (
                        <p className="finance-ops__muted">Loading recent activities…</p>
                    ) : auditJobs.length === 0 ? (
                        <p className="finance-ops__muted">
                            No activities yet. Queue a bill run from Pilots, or apply a payment
                            from Bills / Funding Requests.
                        </p>
                    ) : (
                        <div className="table-wrapper finance-ops__table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Activity</th>
                                        <th>Who</th>
                                        <th>Account</th>
                                        <th>Status</th>
                                        <th>Cycle / correlation</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {auditJobs.map((job) => (
                                        <tr key={job.id}>
                                            <td>{new Date(job.createdAt).toLocaleString()}</td>
                                            <td>
                                                {jobTypeLabel(job.type)}
                                                <div className="finance-ops__sub">{job.type}</div>
                                            </td>
                                            <td className="finance-ops__who">
                                                {job.requestedBy || "—"}
                                            </td>
                                            <td>
                                                {job.accountName || "—"}
                                                <div className="finance-ops__sub">
                                                    {job.accountHccId || job.accountKey || ""}
                                                </div>
                                            </td>
                                            <td className="status-td">
                                                <span className={statusPill(job.status)}>
                                                    {job.status}
                                                </span>
                                            </td>
                                            <td>
                                                {job.cycleId || "—"}
                                                {job.hrpCorrelationId ? (
                                                    <div className="finance-ops__sub">
                                                        cvc {job.hrpCorrelationId}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="finance-ops__error-cell">
                                                {job.error || "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <Drawer
                open={infoOpen}
                onClose={() => setInfoOpen(false)}
                direction="right"
                size={INFO_DRAWER_SIZE}
                duration={200}
                className="finance-ops__drawer"
                overlayClassName="finance-ops__drawer-overlay"
            >
                <div className="finance-ops__panel">
                    <header className="finance-ops__panel-head">
                        <div>
                            <p className="finance-ops__panel-kicker">Finance ops</p>
                            <h2>About this board</h2>
                        </div>
                        <button
                            type="button"
                            className="finance-ops__panel-close"
                            onClick={() => setInfoOpen(false)}
                        >
                            Close
                        </button>
                    </header>
                    <div className="finance-ops__panel-body">
                        <FinanceOpsInfo />
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default FinanceOpsDashboard;
